<template>
	<FlightContainer
		class="current-offer"
		button-theme="extra"
		@show-flight-details="showModal"
	>
		<template #header>
			<h3 class="current-offer__flight-header">{{ translate('alternativeFlights.currentOffer') }}</h3>
		</template>
		<template #outbound>
			<FlightSegment :flight-segments="offerData.OutboundFlightSegments" />
		</template>
		<template #inbound>
			<FlightSegment :flight-segments="offerData.InboundFlightSegments" />
		</template>
		<template #right>
			<div class="current-offer__right-container">
				<FeatureList :offer="offerData" />
				<img
					v-if="offerData.TourOperator"
					class="current-offer__tour-operator"
					:src="offerData.TourOperator.Image"
					:alt="offerData.TourOperator.Name"
					:title="offerData.TourOperator.Name"
				/>
			</div>
		</template>
		<template #price>
			<PriceRow
				v-if="price.amount"
				class="current-offer__price"
				:price-props="{ price: price.amount, currency: price.currency, showDecimals: true, theme: 'accent' }"
				font-size="extralarge"
			/>
		</template>
		<template #button>
			<RoundedButton
				class="current-offer__booking"
				theme="extra"
				as="a"
				:href="checkoutUrl"
			>
				{{ translate('offer.addToCart') }}
			</RoundedButton>
		</template>
	</FlightContainer>
	<OfferDetailsModal
		ref="flightDetailsModal"
		class="alternative-flight__flight-details-modal"
		:title="translate('offer.flight.details')"
	>
		<div v-if="offerData?.InboundFlightSegments && offerData?.OutboundFlightSegments">
			<CheckoutFlightDetails
				:inbound-flight="offerData.InboundFlightSegments"
				:outbound-flight="offerData.OutboundFlightSegments"
				:has-stopover="!!(offerData?.InboundFlightSegments.length > 1 || offerData.OutboundFlightSegments.length > 1)"
			/>
		</div>
	</OfferDetailsModal>
</template>

<script setup lang="ts">
import type { MergedOfferItemData } from '@/interfaces/offer';
import FeatureList from '@lmt-rpb/FeatureList/FeatureList.vue';
import FlightContainer from './FlightContainer.vue';
import FlightSegment from './FlightSegment.vue';
import RoundedButton from '@lmt-rpb/RoundedButton/RoundedButton.vue';
import PriceRow from './PriceRow.vue';
import { useTemplateRef } from 'vue';
import OfferDetailsModal from '@lmt-rpb/OfferDetailsModal/OfferDetailsModal.vue';
import CheckoutFlightDetails from '@lmt-rpb/CheckoutFlightDetails/CheckoutFlightDetails.vue';
import { useTranslations } from '@helper/composables/translations';

type Props = {
	offerData: MergedOfferItemData;
	checkoutUrl: string;
	price: { amount: number | null; currency: 'EUR' | 'CHF' | undefined };
};

const { translate } = useTranslations();

const { offerData } = defineProps<Props>();
const flightDetailsModal = useTemplateRef('flightDetailsModal');

const showModal = (): void => {
	flightDetailsModal.value?.open();
};
</script>

<style lang="scss" scoped>
.current-offer {
	&__flight-header {
		font-weight: $font-weight-bold;
		font-size: $font-small-1;
		line-height: 2.179rem;
		padding: 0;
		margin: 0;
		margin-bottom: 0.8rem;
		text-align: left;
	}

	&__right-container {
		position: relative;
		max-width: 30.8rem;
	}

	&__tour-operator {
		position: absolute;
		right: 0;
		top: 0;
		margin-top: 0.4rem;
	}

	:deep(.feature-list) {
		text-align: left;
		font-weight: $font-weight-regular;

		.feature-list__duration {
			font-size: $font-small-1;
			line-height: 2.7rem;
		}

		.offer-feature,
		.flex-feature {
			font-size: $font-small-2;
			line-height: 2.286rem;
			margin-bottom: 0.4rem !important;
		}
	}

	&__booking.button--extra {
		font-size: $font-small-2;
	}

	&__price {
		margin-bottom: 1.6rem;
	}
}

@media screen and (width >= $breakpoint-small) {
	.current-offer {
		&__flight-header {
			margin-bottom: 1.6rem;
		}

		&__booking.button--extra {
			font-size: $font-small-1;
		}
	}
}
</style>
